import { useEffect, useState } from 'react'
import {
  getProBookingSettings,
  listBookingExternalCalendars,
  proStripeConnectStatus,
  syncBookingExternalCalendars,
  startGoogleCalendarConnect,
  startProStripeConnect,
  updateProBookingSettings,
} from '../../api/bookings'
import { getProsConsoleSummary } from '../../api/prosConsole'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import TimezoneSelect from '../../components/TimezoneSelect'
import type { BookingSettings, ExternalCalendarConnection, StripeConnectStatus } from '../../types/bookings'

export default function ProsBookingTab() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const handle = me?.profile?.handle || null
  const [settings, setSettings] = useState<BookingSettings | null>(null)
  const [stripe, setStripe] = useState<StripeConnectStatus | null>(null)
  const [calendars, setCalendars] = useState<ExternalCalendarConnection[]>([])
  const [weeklyRangesCount, setWeeklyRangesCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const load = async () => {
    if (!handle) return
    setLoading(true)
    setError(null)
    try {
      const [bookingSettings, stripeStatus, externalCalendars] = await Promise.all([
        getProBookingSettings(handle),
        proStripeConnectStatus(handle),
        listBookingExternalCalendars({ provider_type: 'PRO', provider_public_id: handle }).catch(() => [] as ExternalCalendarConnection[]),
      ])
      const summary = await getProsConsoleSummary().catch(() => null)
      setSettings(bookingSettings)
      setStripe(stripeStatus)
      setCalendars(externalCalendars)
      setWeeklyRangesCount(Number(summary?.counts?.weekly_ranges || 0))
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load booking settings.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!handle) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle])

  const onSave = async () => {
    if (!handle || !settings) return
    const nextErrors: Record<string, string> = {}
    if (!String(settings.timezone || '').trim()) nextErrors.timezone = 'Timezone is required.'
    if (!Number.isFinite(settings.slot_duration_minutes) || settings.slot_duration_minutes < 15) nextErrors.slot_duration_minutes = 'Slot duration must be at least 15 minutes.'
    if (!Number.isFinite(settings.booking_window_days) || settings.booking_window_days < 1) nextErrors.booking_window_days = 'Booking window must be at least 1 day.'
    if (!Number.isFinite(settings.buffer_minutes) || settings.buffer_minutes < 0) nextErrors.buffer_minutes = 'Buffer must be non-negative.'
    if (!Number.isFinite(settings.lead_time_minutes) || settings.lead_time_minutes < 0) nextErrors.lead_time_minutes = 'Lead time must be non-negative.'
    if (!Number.isFinite(settings.cancel_cutoff_minutes) || settings.cancel_cutoff_minutes < 0) nextErrors.cancel_cutoff_minutes = 'Cancel cutoff must be non-negative.'
    if (!Number.isFinite(settings.reschedule_cutoff_minutes) || settings.reschedule_cutoff_minutes < 0) nextErrors.reschedule_cutoff_minutes = 'Reschedule cutoff must be non-negative.'
    setFieldErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const updated = await updateProBookingSettings(handle, {
        timezone: settings.timezone,
        slot_duration_minutes: settings.slot_duration_minutes,
        buffer_minutes: settings.buffer_minutes,
        lead_time_minutes: settings.lead_time_minutes,
        booking_window_days: settings.booking_window_days,
        allow_customer_cancel: settings.allow_customer_cancel,
        allow_customer_reschedule: settings.allow_customer_reschedule,
        cancel_cutoff_minutes: settings.cancel_cutoff_minutes,
        reschedule_cutoff_minutes: settings.reschedule_cutoff_minutes,
        is_enabled: settings.is_enabled,
      })
      setSettings(updated)
      setSuccess('Booking settings saved.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save booking settings.')
    } finally {
      setSaving(false)
    }
  }

  const connectStripe = async () => {
    if (!handle) return
    setSaving(true)
    setError(null)
    try {
      const data = await startProStripeConnect(handle)
      if (data?.url) window.location.assign(data.url)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to start Stripe connect.')
    } finally {
      setSaving(false)
    }
  }

  const connectGoogle = async () => {
    if (!settings) return
    setSaving(true)
    setError(null)
    try {
      const url = await startGoogleCalendarConnect({
        provider_type: 'PRO',
        provider_id: settings.host_id,
        provider_public_id: handle || undefined,
        return_url: window.location.href,
      })
      if (url) window.location.assign(url)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to connect Google Calendar.')
    } finally {
      setSaving(false)
    }
  }

  const onSyncCalendars = async () => {
    if (!handle || calendars.length === 0) return
    setSyncing(true)
    setError(null)
    try {
      await syncBookingExternalCalendars({ provider_type: 'PRO', provider_public_id: handle, days: 31 })
      await load()
      setSuccess('External calendars synced.')
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to sync external calendars.')
    } finally {
      setSyncing(false)
    }
  }

  if (loading) return <div className="text-sm text-white/70">Loading booking settings...</div>

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">Stripe Connect (deposits)</div>
            <div className="mt-1 text-xs text-white/65">Required only if you want booking deposits.</div>
          </div>
          <span className={`rounded-full border px-2 py-0.5 text-xs ${stripe?.connected ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-white/15 bg-white/5 text-white/75'}`}>
            {stripe?.connected ? (stripe?.charges_enabled ? 'Connected' : 'Pending') : 'Not connected'}
          </span>
        </div>
        <button type="button" onClick={() => void connectStripe()} disabled={saving} className="mt-3 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10 disabled:opacity-60">
          Connect Stripe
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 text-sm font-semibold text-white">Booking settings</div>
        {settings ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="text-sm">Timezone
              <TimezoneSelect value={settings.timezone} onChange={(next) => setSettings((prev) => (prev ? { ...prev, timezone: next } : prev))} className="mt-1" />
              {fieldErrors.timezone ? <div className="mt-1 text-xs text-red-300">{fieldErrors.timezone}</div> : null}
            </label>
            <label className="text-sm">Lead time (minutes)
              <input type="number" min={0} value={settings.lead_time_minutes} onChange={(e) => setSettings((prev) => (prev ? { ...prev, lead_time_minutes: Number(e.target.value || 0) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              <div className="mt-1 text-[11px] text-white/55">Minimum time before a booking can start.</div>
              {fieldErrors.lead_time_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.lead_time_minutes}</div> : null}
            </label>
            <label className="text-sm">Buffer (minutes)
              <input type="number" min={0} value={settings.buffer_minutes} onChange={(e) => setSettings((prev) => (prev ? { ...prev, buffer_minutes: Number(e.target.value || 0) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              <div className="mt-1 text-[11px] text-white/55">Time between appointments.</div>
              {fieldErrors.buffer_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.buffer_minutes}</div> : null}
            </label>
            <label className="text-sm">Booking window (days)
              <input type="number" min={1} value={settings.booking_window_days} onChange={(e) => setSettings((prev) => (prev ? { ...prev, booking_window_days: Number(e.target.value || 1) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              {fieldErrors.booking_window_days ? <div className="mt-1 text-xs text-red-300">{fieldErrors.booking_window_days}</div> : null}
            </label>
            <label className="text-sm">Slot duration (minutes)
              <input type="number" min={1} value={settings.slot_duration_minutes} onChange={(e) => setSettings((prev) => (prev ? { ...prev, slot_duration_minutes: Number(e.target.value || 30) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              {fieldErrors.slot_duration_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.slot_duration_minutes}</div> : null}
            </label>
            <label className="text-sm">Cancel cutoff (minutes)
              <input type="number" min={0} value={settings.cancel_cutoff_minutes} onChange={(e) => setSettings((prev) => (prev ? { ...prev, cancel_cutoff_minutes: Number(e.target.value || 0) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              {fieldErrors.cancel_cutoff_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.cancel_cutoff_minutes}</div> : null}
            </label>
            <label className="text-sm">Reschedule cutoff (minutes)
              <input type="number" min={0} value={settings.reschedule_cutoff_minutes} onChange={(e) => setSettings((prev) => (prev ? { ...prev, reschedule_cutoff_minutes: Number(e.target.value || 0) } : prev))} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" />
              {fieldErrors.reschedule_cutoff_minutes ? <div className="mt-1 text-xs text-red-300">{fieldErrors.reschedule_cutoff_minutes}</div> : null}
            </label>
            <label className="inline-flex items-center gap-2 text-sm md:col-span-3">
              <input type="checkbox" checked={settings.is_enabled} onChange={(e) => setSettings((prev) => (prev ? { ...prev, is_enabled: e.target.checked } : prev))} />
              Booking enabled
            </label>
            <div className="md:col-span-3 text-xs text-white/60">Turn this on to accept booking requests from your public card.</div>
            {!settings.is_enabled ? (
              <div className="md:col-span-3 rounded-lg border border-amber-400/30 bg-amber-500/10 p-2 text-xs text-amber-100">
                Booking is currently off. Turn it on to accept booking requests.
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="mt-3">
          <button type="button" onClick={() => void onSave()} disabled={saving || !settings} className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
            {saving ? 'Saving...' : 'Save booking settings'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold text-white">External calendars</div>
        <div className="mt-2 text-xs text-white/60">Read-only sync status</div>
        <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="text-sm text-white/85">Weekly hours</div>
          {weeklyRangesCount > 0 ? (
            <div className="mt-1 text-xs text-white/65">{weeklyRangesCount} weekly ranges configured.</div>
          ) : (
            <div className="mt-1 text-xs text-white/65">No weekly hours configured yet. At least one open slot is required to enable booking.</div>
          )}
          <button type="button" onClick={() => window.location.assign('/settings/pixe-pros?tab=booking')} className="mt-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10">
            Set weekly hours
          </button>
        </div>
        {calendars.length === 0 ? (
          <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="text-sm text-white/80">Not connected</div>
            <button type="button" onClick={() => void connectGoogle()} disabled={saving || !settings || !settings?.is_enabled} className="mt-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60">
              Connect Google Calendar
            </button>
          </div>
        ) : null}
        {calendars.length > 0 ? (
          <button type="button" onClick={() => void onSyncCalendars()} disabled={syncing || !settings?.is_enabled} className="mt-2 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 disabled:opacity-60">
            {syncing ? 'Syncing...' : 'Sync now'}
          </button>
        ) : null}
        <div className="mt-2 space-y-2">
          {calendars.map((row) => (
            <div key={row.id} className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm">
              <div className="font-semibold text-white">{row.kind.toUpperCase()}</div>
              <div className="text-xs text-white/65">{row.provider_email || 'No email'} - {row.needs_reauth ? 'Needs re-auth' : 'Connected'}{row.updated_at ? ` - Last synced ${new Date(row.updated_at).toLocaleString()}` : ''}</div>
            </div>
          ))}
        </div>
      </div>
      {success ? <div className="text-sm text-emerald-300">{success}</div> : null}
      {error ? <div className="text-sm text-red-300">{error}</div> : null}
    </div>
  )
}
